// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { LayoutGrid, List, Network, Search, TriangleAlert, type LucideIcon } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { AppGridCard } from '@/components/apps-list/AppGridCard';
import { AppsAdminBanner } from '@/components/apps-list/AppsAdminBanner';
import { AppListCard } from '@/components/apps-list/AppListCard';
import { AvailableIntegrations } from '@/components/apps-list/AvailableIntegrations';
import { UsageSignal } from '@/components/apps-list/UsageSignal';
import {
  LastUsedSignal,
  ScheduledToPauseBadge,
  lastUsedSortValue,
} from '@/components/apps-list/lastUsed';
import { AppsEmptyState } from '@/components/apps-list/AppsEmptyState';
import { AppsFilterBar, type AppsFilterState } from '@/components/apps-list/AppsFilterBar';
import { AppsSortMenu } from '@/components/apps-list/AppsSortMenu';
import {
  bucketCount,
  filterAppsByState,
  isFleetBucket,
  isServerSortKey,
  orderPage,
  parseSort,
  type TagMode,
} from '@/components/apps-list/filtering';
import { SegmentedTabs, type SegmentedTabsItem } from '@/components/greenlight/SegmentedTabs';
import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { CardGrid } from '@/components/greenlight/CardGrid';
import { PIPELINE_META, statusMeta } from '@/components/greenlight/appStatusMeta';
import { DataList, type DataListColumn } from '@/components/greenlight/DataList';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { IntegrationStack } from '@/components/greenlight/IntegrationStack';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { OwnerAvatar } from '@/components/greenlight/OwnerAvatar';
import { pipelineIcon } from '@/components/greenlight/pipelineIcon';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { RefreshWarning } from '@/components/greenlight/RefreshWarning';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import {
  isTransientQueryError,
  liveQueryOptions,
  metricsQueryOptions,
  statusPollInterval,
} from '@/lib/polling';
import { useSessionCursorStack } from '@/lib/useSessionCursorStack';
import { useCurrentUser } from '@/lib/auth';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { cn } from '@/lib/utils';
import {
  type AppListItem,
  type AppStatus,
  type AppUsageSummaryItem,
  type FleetBucket,
} from '@greenlight/shared';

// The fleet map (graph renderer + layout) is opt-in and IT-only, so it is
// code-split out of the main bundle and loaded only when the Map view is opened.
const AppFleetMap = React.lazy(() => import('@/components/apps-map/AppFleetMap'));

const PAGE_SIZE = 25;

// Params that narrow or reorder the server-side result set. A cursor is only meaningful
// within the result set and ordering that issued it — the server rejects a mismatched one —
// so changing any of these restarts pagination instead of resuming it.
const FILTER_PARAMS = new Set(['status', 'bucket', 'q', 'tags', 'tagmode', 'sort']);

// Search runs server-side, so hold each keystroke briefly rather than issuing a
// query per character.
const SEARCH_DEBOUNCE_MS = 250;

// One shared trailing window for the list signal and the map's traffic
// weighting, so "busy" means the same thing in both views.
const USAGE_WINDOW_DAYS = 7;

// The integrations column/row is fully built but fed by placeholder data until the
// integrations↔apps many-to-many read-side lands and the real server populates
// `AppListItem.integrations`. Hidden until then — flip to `true` to re-enable.
const SHOW_INTEGRATIONS: boolean = false;

const Dash = () => <span className="text-muted-foreground">—</span>;

function viewLabel(Icon: LucideIcon, label: string): React.ReactNode {
  return (
    <span className="inline-flex items-center" title={label}>
      <Icon className="size-4" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

// Last deploy renders as coarse elapsed time ("2 h ago") per the dashboard
// design; exact timestamps belong in the app-detail view.
function formatLastDeploy(iso: string | null): React.ReactNode {
  if (!iso) return <Dash />;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return <Dash />;
  return <span className="text-muted-foreground">{relativeTime(iso)}</span>;
}

export function AppsPage(): React.JSX.Element {
  usePageTitle('Apps');
  const user = useCurrentUser();
  const navigate = useNavigate();
  const isIT = user?.role === 'org_admin' || user?.role === 'admin';

  const [searchParams, setSearchParams] = useSearchParams();
  const cursor = searchParams.get('cursor') ?? undefined;
  const status = (searchParams.get('status') ?? '') as AppStatus | '';
  const bucketParam = searchParams.get('bucket');
  const bucket: FleetBucket | '' = isFleetBucket(bucketParam) ? bucketParam : '';
  const q = searchParams.get('q') ?? '';
  const sort = parseSort(searchParams.get('sort'));
  // Multi-select tag filter (ADR-0032): ?tags is a comma-joined list; ?tagmode
  // switches inclusive (ANY, default) vs exclusive (ALL).
  const tags = (searchParams.get('tags') ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const tagMode: TagMode = searchParams.get('tagmode') === 'all' ? 'all' : 'any';
  // Apps whose latest run failed — a different question from lifecycle status, since
  // an app can be active and serving its last good image while its newest run is red.
  // `usage` isn't an apps column — it's a separate aggregate — so it stays a page-local sort
  // while slug/status reorder the whole result set in SQL (docs/28 §/apps).
  const serverSort = sort && isServerSortKey(sort.key) ? { ...sort, key: sort.key } : null;

  // View mode (List · Grid · Map) is deep-linked via ?view so a view is
  // shareable (docs/28 §/apps). List and Grid are catalog-safe and open to every
  // role; Map exposes cross-app governance topology, so it is IT-only and a
  // ?view=map from a citizen developer falls back to the list.
  const viewParam = searchParams.get('view');
  const view: 'list' | 'grid' | 'map' =
    viewParam === 'grid' ? 'grid' : isIT && viewParam === 'map' ? 'map' : 'list';

  // Refresh-safe Previous trail; cleared whenever the view is back on page one.
  const trail = useSessionCursorStack('apps');
  const clearTrail = trail.clear;
  React.useEffect(() => {
    if (!cursor) clearTrail();
  }, [cursor, clearTrail]);

  const setParam = React.useCallback(
    (key: string, value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value == null || value === '') next.delete(key);
        else next.set(key, value);
        if (FILTER_PARAMS.has(key)) next.delete('cursor');
        return next;
      });
    },
    [setSearchParams],
  );

  // Covers deep links and browser navigation too, not just the setters above:
  // whenever the result set changes identity, the forward-only history of
  // cursors collected for the previous one no longer points anywhere.
  // Same set of dimensions as the query key above: this is what resets the
  // Previous trail, and a cursor from a different result set is not resumable.
  const filterSignature = `${status}|${bucket}|${q}|${tags.join(',')}|${tagMode}|${serverSort?.key ?? ''}:${serverSort?.direction ?? ''}`;
  // Clear only on a real signature change — running on mount too would wipe
  // the session-restored trail that makes Previous survive a refresh.
  const lastSignatureRef = React.useRef(filterSignature);
  React.useEffect(() => {
    if (lastSignatureRef.current === filterSignature) return;
    lastSignatureRef.current = filterSignature;
    clearTrail();
  }, [filterSignature, clearTrail]);

  const setView = (next: 'list' | 'grid' | 'map') =>
    setParam('view', next === 'list' ? null : next);

  // Every filter is a query param, so a filtered view narrows the whole org
  // fleet rather than the loaded page (docs/28 §/apps).
  const debouncedQ = useDebouncedValue(q, SEARCH_DEBOUNCE_MS);
  const filterQuery = {
    ...(status ? { status } : {}),
    ...(bucket ? { bucket } : {}),
    // Comma-joined rather than a repeated param: the ts-rest client serializes
    // arrays as `tag[0]=…`, which Fastify's querystring parser does not read
    // back as an array.
    ...(tags.length ? { tag: tags.join(','), tag_mode: tagMode } : {}),
    ...(debouncedQ ? { q: debouncedQ } : {}),
  };
  const listQuery = {
    limit: PAGE_SIZE,
    include_hidden: 'true' as const,
    ...filterQuery,
    ...(serverSort ? { sort: serverSort.key, order: serverSort.direction } : {}),
    ...(cursor ? { cursor } : {}),
  };
  const queryKey = [
    'apps',
    cursor ?? null,
    status,
    bucket,
    tags.join(','),
    tagMode,
    debouncedQ,
    // Every dimension `listQuery` narrows on has to appear here, or a toggle
    // reuses the previous result set's cache entry and the list contradicts the
    // URL and the chip.
    serverSort ? `${serverSort.key}:${serverSort.direction}` : '',
  ];
  const { data, isLoading, isError, error } = api.apps.list.useQuery(
    queryKey,
    { query: listQuery },
    {
      ...liveQueryOptions,
      queryKey,
      refetchInterval: ({ state }) =>
        statusPollInterval(
          state.data?.body.items.flatMap((app) => [app.status, app.last_pipeline_status]) ?? [],
        ),
    },
  );

  // Org-wide status counts for the attention callout and the bucket chips. Gated
  // to IT, like the governance columns below — citizen developers see catalog
  // metadata only.
  const summaryQuery = api.apps.summary.useQuery(['apps-summary'], undefined, {
    ...liveQueryOptions,
    queryKey: ['apps-summary'],
    enabled: isIT,
    refetchInterval: ({ state }) =>
      statusPollInterval(
        Object.entries(state.data?.body.by_status ?? {})
          .filter(([, count]) => count > 0)
          .map(([status]) => status),
      ),
  });
  const summary = isIT ? summaryQuery.data?.body : undefined;
  const counts = summary?.by_status;
  const attentionCount = summary?.needs_attention ?? 0;
  const failingPipelines = summary?.pipeline_failures.total ?? 0;
  const attentionOn = bucket === 'needs_attention';
  // Needs attention also counts failing pipelines, so it takes the summary's
  // distinct-app total rather than a sum of statuses.
  const bucketCounts = React.useMemo(
    () =>
      counts && {
        deploying: bucketCount(counts, 'deploying'),
        needs_attention: attentionCount,
        suspended: bucketCount(counts, 'suspended'),
        deleted: bucketCount(counts, 'deleted'),
      },
    [counts, attentionCount],
  );

  // Fleet usage aggregate (the /auth/check-metered rollups): the per-row usage
  // signal, the `usage` sort, and the map's traffic weighting all read this one
  // IT-gated summary. Zero-valued rows exist for every live app, so a missing
  // entry means the read hasn't landed, not "no traffic".
  const usageQuery = api.usage.getSummary.useQuery(
    ['apps-usage-summary', USAGE_WINDOW_DAYS],
    { query: { window_days: USAGE_WINDOW_DAYS } },
    { ...metricsQueryOptions, queryKey: ['apps-usage-summary', USAGE_WINDOW_DAYS], enabled: isIT },
  );
  const usageData = usageQuery.data;
  const usageByApp = React.useMemo(() => {
    // A 200 without an items array is a malformed response, not zero traffic —
    // reading it as data would crash the whole list over a secondary signal.
    const items =
      usageData?.status === 200 && Array.isArray(usageData.body.items) ? usageData.body.items : [];
    return new Map<string, AppUsageSummaryItem>(items.map((u) => [u.app_id, u]));
  }, [usageData]);
  // Sorts on what the column leads with, so the order matches what the eye reads.
  const activeUsersByApp = React.useMemo(
    () => new Map<string, number>([...usageByApp.values()].map((u) => [u.app_id, u.active_users])),
    [usageByApp],
  );
  // Last-used ordering is over the same summary, keyed on the timestamp the
  // column renders rather than the volume the Usage column does.
  const lastUsedByApp = React.useMemo(
    () =>
      new Map<string, number>(
        [...usageByApp.values()].map((u) => [u.app_id, lastUsedSortValue(u)]),
      ),
    [usageByApp],
  );

  // Fleet topology for the Map view. Fetched only when the Map is open (and only
  // for IT), so the List view pays nothing. The map consumes the same status /
  // bucket / search filter state as the list (docs/28 §/apps) but imposes its
  // own RBAC-lane ordering, so it reuses the shared predicate, not the sort.
  const graphQuery = api.apps.fleetGraph.useQuery(['apps-graph'], undefined, {
    ...liveQueryOptions,
    queryKey: ['apps-graph'],
    enabled: isIT && view === 'map',
    refetchInterval: ({ state }) =>
      statusPollInterval(state.data?.body.apps.map((app) => app.status) ?? []),
  });
  const rawGraphApps = graphQuery.data?.body.apps ?? [];
  const graphApps = filterAppsByState(rawGraphApps, { status, bucket, q });

  const rawItems = data?.body.items ?? [];
  const nextCursor = data?.body.next_cursor ?? null;
  const items = orderPage(rawItems, {
    sort: serverSort ? null : sort,
    usageByApp: activeUsersByApp,
    lastUsedByApp,
  });
  // An empty page means an empty fleet only when nothing narrowed it — the server filters now, so
  // a filter matching nothing must not read as "you have no apps".
  //
  // Derived from the params the loaded page was actually fetched with, not from what is currently
  // typed. Search trails the input by a debounce, so clearing a no-match search zeroes the live
  // count while the stale empty result is still on screen; reading the live count here would flash
  // the getting-started panel at someone whose fleet is full.
  const narrowedRequest = Object.keys(filterQuery).length > 0;
  const fleetIsEmpty = rawItems.length === 0 && !narrowedRequest;
  // The empty state depends on whether the org has a plugin marketplace yet.
  const settingsQuery = api.settings.get.useQuery(['settings'], undefined, {
    queryKey: ['settings'],
    enabled: fleetIsEmpty && !isLoading && !isError,
  });

  const availableTags = React.useMemo(() => {
    const seen = new Set<string>();
    for (const app of rawItems) {
      if (app.status === 'deleted') continue;
      for (const tag of app.tags ?? []) seen.add(tag);
    }
    for (const tag of tags) seen.add(tag);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [rawItems, tags]);

  // The filter bar edits bucket and tags together; status stays a
  // deep-link-only exact filter and is cleared once the bar takes over.
  function setFilterState(next: AppsFilterState) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const put = (key: string, value: string | null) =>
        value ? params.set(key, value) : params.delete(key);
      put('bucket', next.bucket || null);
      put('tags', next.tags.join(',') || null);
      put('tagmode', next.tags.length && next.tagMode === 'all' ? 'all' : null);
      params.delete('status');
      params.delete('cursor');
      return params;
    });
  }

  function onSortChange(key: string) {
    const direction = sort?.key === key && sort.direction === 'asc' ? 'desc' : 'asc';
    setParam('sort', `${key}:${direction}`);
  }

  function goNext() {
    if (!nextCursor) return;
    trail.push(cursor ?? '');
    setParam('cursor', nextCursor);
  }

  function goPrev() {
    const prev = trail.pop();
    // No trail (a shared link in a fresh tab): fall back to the first page
    // rather than dead-ending on the cursor page.
    setParam('cursor', prev === undefined || prev === '' ? null : prev);
  }

  function clearFilters() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('status');
      next.delete('bucket');
      next.delete('q');
      next.delete('tags');
      next.delete('tagmode');
      next.delete('cursor');
      return next;
    });
  }

  /**
   * The alert counts the whole fleet and offers to show that count, so the list it
   * opens must match: filters that would hide part of the set are cleared. The Map
   * renders its own projection, so the action also returns to the list.
   */
  function showAllNeedingAttention() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const param of ['status', 'q', 'tags', 'tagmode', 'pipeline', 'cursor', 'view']) {
        next.delete(param);
      }
      next.set('bucket', 'needs_attention');
      return next;
    });
  }

  const columns: DataListColumn<AppListItem>[] = [
    {
      key: 'slug',
      header: 'App',
      primary: true,
      sortable: true,
      render: (app) => (
        <div className="flex items-center gap-3">
          <AppIconTile iconUrl={app.icon_url} name={app.name} slug={app.slug} size={40} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="flex items-center gap-1.5 font-medium text-foreground">
              <span className="truncate">{app.name}</span>
            </span>
            <MonoValue className="w-fit text-[11px]">{app.serving_subdomain ?? app.slug}</MonoValue>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      // The inactivity flag rides alongside the status rather than replacing it:
      // an app is Active *and* scheduled to pause, and collapsing the two would
      // hide either what it is doing now or what is about to happen to it.
      render: (app) => {
        const meta = statusMeta(app.status);
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>
            {app.scheduled_to_pause && <ScheduledToPauseBadge />}
          </span>
        );
      },
    },
    {
      key: 'owner',
      header: 'Owner',
      // Cap + truncate so a long email can't force the table past its container
      // (full address on hover via title); keeps the row on one line, no scroll.
      className: 'max-w-[200px]',
      render: (app) =>
        app.owner_email ? (
          <div className="flex min-w-0 items-center gap-2">
            <OwnerAvatar name={app.owner_name} email={app.owner_email} />
            <span className="min-w-0 truncate text-sm text-foreground" title={app.owner_email}>
              {app.owner_email}
            </span>
          </div>
        ) : (
          <Dash />
        ),
    },
    ...(SHOW_INTEGRATIONS
      ? [
          {
            key: 'integrations',
            header: 'Integrations',
            priority: 'lg',
            render: (app: AppListItem) => <IntegrationStack slugs={app.integrations ?? []} />,
          } satisfies DataListColumn<AppListItem>,
        ]
      : []),
  ];

  if (isIT) {
    columns.push({
      key: 'usage',
      header: `Usage (${USAGE_WINDOW_DAYS}d)`,
      priority: 'lg',
      sortable: true,
      render: (app) => (
        <UsageSignal usage={usageByApp.get(app.app_id)} windowDays={USAGE_WINDOW_DAYS} />
      ),
    });
    // Governance, not catalog metadata: how long an app has gone unused is the
    // question behind pausing it, so it sits with the other IT-only columns and
    // is hidden from citizen developers like them.
    columns.push({
      key: 'lastUsed',
      header: 'Last Used',
      priority: 'lg',
      sortable: true,
      render: (app) => <LastUsedSignal usage={usageByApp.get(app.app_id)} />,
    });
  }

  columns.push({
    // When the app last changed and how that change went, read together: the
    // pipeline outcome is what qualifies the timestamp beside it.
    key: 'lastUpdated',
    header: 'Last Updated',
    priority: 'lg',
    render: (app) => {
      if (!app.last_deploy_at && !app.last_pipeline_status) return <Dash />;
      const meta = app.last_pipeline_status
        ? (PIPELINE_META[app.last_pipeline_status] ?? {
            variant: 'inactive' as StatusBadgeVariant,
            label: app.last_pipeline_status,
          })
        : null;
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          {meta &&
            (() => {
              const { Icon, className, spin } = pipelineIcon(meta.variant);
              const labelText = `Last pipeline: ${meta.label}`;
              return (
                <span className={cn('inline-flex', className)} title={labelText}>
                  <Icon
                    className={cn('size-4', spin && 'motion-safe:animate-spin')}
                    role="img"
                    aria-label={labelText}
                  />
                </span>
              );
            })()}
          {formatLastDeploy(app.last_deploy_at)}
        </span>
      );
    },
  });

  const renderCard = (app: AppListItem): React.ReactNode => (
    <AppListCard
      app={app}
      isIT={isIT}
      showIntegrations={SHOW_INTEGRATIONS}
      usage={usageByApp.get(app.app_id)}
      usageWindowDays={USAGE_WINDOW_DAYS}
    />
  );

  // Cursor pager shared by the List and Grid views (both page the same query).
  const pager = (
    <div className="flex items-center justify-end gap-2 pt-2">
      <Button variant="outline" size="sm" onClick={goPrev} disabled={!cursor}>
        Previous
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={goNext}
        disabled={!nextCursor}
        data-testid="apps-next"
      >
        Next
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="apps-page">
      <div>
        <Eyebrow>App inventory</Eyebrow>
        <h1 className="text-2xl font-semibold tracking-[-0.02em] md:text-[28px]">Apps</h1>
      </div>

      {view !== 'map' && isError && data?.status === 200 && isTransientQueryError(error) && (
        <RefreshWarning />
      )}
      {view === 'map' &&
        graphQuery.isError &&
        graphQuery.data?.status === 200 &&
        isTransientQueryError(graphQuery.error) && <RefreshWarning />}

      {user?.role === 'user' && <AvailableIntegrations />}

      {user?.role === 'org_admin' && summary && (
        <AppsAdminBanner
          summary={summary}
          usage={usageData?.status === 200 ? usageData.body : undefined}
        />
      )}

      {/* Org-wide health alert (IT only), read from the summary rather than the
          loaded page. The total counts each app once; the breakdown's parts can
          overlap (a failed app can also have a red pipeline), so they need not
          sum to it. */}
      {attentionCount > 0 && counts && (
        <Alert
          variant={counts.failed > 0 || failingPipelines > 0 ? 'destructive' : 'warning'}
          className="md:has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr_auto]"
          data-testid="apps-health-alert"
        >
          <TriangleAlert aria-hidden />
          <AlertTitle className="font-semibold">
            {attentionCount === 1
              ? '1 app needs attention'
              : `${attentionCount} apps need attention`}
          </AlertTitle>
          <AlertDescription>
            {[
              counts.failed > 0 && `${counts.failed} failed`,
              counts.degraded > 0 && `${counts.degraded} degraded`,
              failingPipelines > 0 &&
                `${failingPipelines} failing ${failingPipelines === 1 ? 'pipeline' : 'pipelines'}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </AlertDescription>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="col-start-2 mt-2 h-11 justify-self-start text-foreground md:col-start-3 md:row-span-2 md:row-start-1 md:mt-0 md:h-8 md:self-center"
            onClick={attentionOn ? () => setParam('bucket', null) : showAllNeedingAttention}
            data-testid="apps-health-alert-action"
          >
            {attentionOn ? 'Clear filter' : `View ${attentionCount}`}
          </Button>
        </Alert>
      )}

      {/* Search, Sort and an empty filter bar share the first row with the view
          switcher; once a filter is set the bar takes its own full-width row. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="apps-toolbar">
        <div className="relative min-w-0 flex-1 md:w-[240px] md:flex-none">
          <Search
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            placeholder="Search apps"
            aria-label="Search by name or slug"
            value={q}
            onChange={(e) => setParam('q', e.target.value)}
            className="h-8 pl-9"
          />
        </div>

        {/* The map imposes its own lane order, so it has no sort. */}
        {view !== 'map' && (
          <AppsSortMenu
            sort={sort}
            isIT={isIT}
            onSortChange={(next) => setParam('sort', next ? `${next.key}:${next.direction}` : null)}
          />
        )}

        <AppsFilterBar
          className="data-active:order-last data-active:basis-full"
          state={{ bucket, tags, tagMode }}
          onStateChange={setFilterState}
          availableTags={availableTags}
          bucketCounts={bucketCounts}
          isIT={isIT}
        />

        {/* List and Grid are catalog-safe (all roles); Map is IT-only (docs/28). */}
        <div className="ms-auto" data-testid="apps-view-switcher">
          <SegmentedTabs
            aria-label="View mode"
            size="sm"
            value={view}
            onValueChange={(v) => setView(v as 'list' | 'grid' | 'map')}
            items={
              [
                { value: 'list', testId: 'apps-view-list', label: viewLabel(List, 'List') },
                {
                  value: 'grid',
                  testId: 'apps-view-grid',
                  label: viewLabel(LayoutGrid, 'Grid'),
                },
                ...(isIT
                  ? [
                      {
                        value: 'map',
                        testId: 'apps-view-map',
                        label: viewLabel(Network, 'Map'),
                      },
                    ]
                  : []),
              ] satisfies SegmentedTabsItem[]
            }
          />
        </div>
      </div>

      {view === 'map' ? (
        graphQuery.isError &&
        (graphQuery.data?.status !== 200 || !isTransientQueryError(graphQuery.error)) ? (
          <Alert variant="destructive" data-testid="apps-map-error">
            <AlertTitle>Couldn't load the map</AlertTitle>
            <AlertDescription>
              Something went wrong fetching the fleet topology. Retry in a moment.
            </AlertDescription>
          </Alert>
        ) : graphQuery.isLoading ? (
          <Skeleton className="h-[70vh] min-h-[460px] w-full" data-testid="apps-map-loading" />
        ) : rawGraphApps.length === 0 ? (
          <div
            className="rounded-gl border border-border p-8 text-center"
            data-testid="apps-map-empty"
          >
            <p className="text-sm font-medium text-foreground">No live apps to map.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Apps appear here once they're registered through the Greenlight plugin.
            </p>
          </div>
        ) : graphApps.length === 0 ? (
          <div
            className="rounded-gl border border-border p-8 text-center"
            data-testid="apps-map-no-match"
          >
            <p className="text-sm text-muted-foreground">No apps match the current filters.</p>
            <Button variant="ghost" size="sm" className="mt-2" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        ) : (
          <React.Suspense
            fallback={
              <Skeleton className="h-[70vh] min-h-[460px] w-full" data-testid="apps-map-loading" />
            }
          >
            <AppFleetMap
              apps={graphApps}
              truncated={graphQuery.data?.body.truncated ?? false}
              totalApps={graphQuery.data?.body.total_apps ?? graphApps.length}
              onSelectApp={(appId) => navigate(`/apps/${appId}`)}
              // An empty map means the summary hasn't resolved — render baseline
              // with no traffic chrome rather than implying "no traffic".
              usage={usageByApp.size > 0 ? usageByApp : undefined}
              usageWindowDays={USAGE_WINDOW_DAYS}
            />
          </React.Suspense>
        )
      ) : isError && (data?.status !== 200 || !isTransientQueryError(error)) ? (
        <Alert variant="destructive" data-testid="apps-error">
          <AlertTitle>Couldn't load apps</AlertTitle>
          <AlertDescription>
            Something went wrong fetching the catalog. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="space-y-2" data-testid="apps-loading">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : fleetIsEmpty ? (
        settingsQuery.isLoading ? (
          <Skeleton className="h-32 w-full" data-testid="apps-empty-loading" />
        ) : (
          <div data-testid="apps-empty">
            <AppsEmptyState
              role={user?.role}
              agentSetup={
                settingsQuery.data?.status === 200 ? settingsQuery.data.body.agent_setup : undefined
              }
            />
          </div>
        )
      ) : items.length === 0 ? (
        <div
          className="rounded-gl border border-border p-8 text-center"
          data-testid="apps-no-match"
        >
          <p className="text-sm text-muted-foreground">No apps match the current filters.</p>
          <Button variant="ghost" size="sm" className="mt-2" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : view === 'grid' ? (
        <>
          <div data-testid="apps-grid">
            <CardGrid columns={3}>
              {items.map((app) => (
                <AppGridCard key={app.app_id} app={app} />
              ))}
            </CardGrid>
          </div>
          {pager}
        </>
      ) : (
        <>
          <DataList
            ariaLabel="Apps"
            items={items}
            columns={columns}
            rowKey={(app) => app.app_id}
            sort={sort}
            onSortChange={onSortChange}
            onRowActivate={(app) => navigate(`/apps/${app.app_id}`)}
            renderCard={renderCard}
            rowAction={(app) => (
              <Button asChild variant="ghost" size="sm">
                <Link to={`/apps/${app.app_id}`}>Manage</Link>
              </Button>
            )}
          />
          {pager}
        </>
      )}
    </div>
  );
}
